import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseDetails } from '../../mocks/courseDetails';
import Navbar from '../home/components/Navbar';

export default function CoursePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'curriculum'>('overview');

  const course = courseDetails.find(c => c.id === Number(id));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f5]">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1a2332] mb-4">강좌를 찾을 수 없습니다</h1>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#1a2332] text-white rounded-full hover:bg-[#ff6b6b] transition-colors cursor-pointer whitespace-nowrap"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const colorSchemes = [
    { bg: 'from-[#4ecdc4]/20 to-[#4ecdc4]/5', icon: 'text-[#4ecdc4]', accent: '#4ecdc4' },
    { bg: 'from-[#ff6b6b]/20 to-[#ff6b6b]/5', icon: 'text-[#ff6b6b]', accent: '#ff6b6b' },
    { bg: 'from-[#1a2332]/20 to-[#1a2332]/5', icon: 'text-[#1a2332]', accent: '#1a2332' },
    { bg: 'from-[#ffd93d]/20 to-[#ffd93d]/5', icon: 'text-[#ffd93d]', accent: '#ffd93d' }
  ];

  const colorScheme = colorSchemes[(course.id - 1) % 4];

  const icons = ['ri-medal-line', 'ri-book-open-line', 'ri-pencil-line', 'ri-quill-pen-line'];
  const courseIcon = icons[(course.id - 1) % 4];

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className={`pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br ${colorScheme.bg}`}>
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1a2332] transition-colors mb-6 md:mb-8 cursor-pointer text-sm md:text-base"
          >
            <i className="ri-arrow-left-line"></i>
            <span>전체 강좌 보기</span>
          </button>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12">
            {/* 모바일에서 아이콘 먼저 표시 */}
            <div className="w-48 h-48 md:w-80 md:h-80 bg-white rounded-3xl shadow-lg flex items-center justify-center order-first md:order-last flex-shrink-0">
              <i className={`${courseIcon} text-6xl md:text-9xl ${colorScheme.icon}`}></i>
            </div>

            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 md:gap-3 mb-3 md:mb-4">
                <span className="px-3 md:px-4 py-1 md:py-1.5 bg-[#1a2332] text-white text-xs md:text-sm rounded-full font-medium">
                  {course.level}
                </span>
                {course.id === 1 && (
                  <span className="px-3 md:px-4 py-1 md:py-1.5 bg-[#ff6b6b] text-white text-xs md:text-sm rounded-full font-medium">
                    인기
                  </span>
                )}
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-[#1a2332] mb-4 md:mb-6" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                {course.name}
              </h1>

              <p className="text-base md:text-xl text-gray-700 leading-relaxed mb-6 md:mb-8">
                {course.fullDescription}
              </p>

              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 md:gap-6 mb-6 md:mb-8 text-sm md:text-base">
                <div className="flex items-center gap-2 text-gray-600">
                  <i className="ri-time-line text-[#ff6b6b]"></i>
                  <span>{course.schedule}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <i className="ri-group-line text-[#ff6b6b]"></i>
                  <span>정원 {course.capacity}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <i className="ri-calendar-line text-[#ff6b6b]"></i>
                  <span>{course.duration}</span>
                </div>
              </div>

              <div className="flex items-center justify-center md:justify-start gap-4">
                <button
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate('/#contact');
                    }
                  }}
                  className="px-6 md:px-8 py-3 md:py-4 bg-[#1a2332] text-white text-sm md:text-base rounded-full hover:bg-[#ff6b6b] transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  수강 상담 신청
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="sticky top-[60px] md:top-[72px] z-40 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex gap-4 md:gap-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-3 md:py-4 px-2 border-b-2 transition-colors cursor-pointer whitespace-nowrap text-sm md:text-base ${
                activeTab === 'overview'
                  ? 'border-[#ff6b6b] text-[#1a2332] font-semibold'
                  : 'border-transparent text-gray-500 hover:text-[#1a2332]'
              }`}
            >
              강좌 소개
            </button>
            <button
              onClick={() => setActiveTab('curriculum')}
              className={`py-3 md:py-4 px-2 border-b-2 transition-colors cursor-pointer whitespace-nowrap text-sm md:text-base ${
                activeTab === 'curriculum'
                  ? 'border-[#ff6b6b] text-[#1a2332] font-semibold'
                  : 'border-transparent text-gray-500 hover:text-[#1a2332]'
              }`}
            >
              커리큘럼
            </button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-10 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {activeTab === 'overview' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                {/* Features */}
                <div className="bg-white rounded-2xl p-5 md:p-8 shadow-sm">
                  <h2 className="text-xl md:text-2xl font-bold text-[#1a2332] mb-4 md:mb-6" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                    강좌 특징
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {course.features.map((feature, index) => (
                      <div key={index} className="bg-[#f7f7f5] rounded-xl p-4 md:p-5 text-center">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-[#ff6b6b]/10 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                          <i className={`ri-check-line text-xl md:text-2xl text-[#ff6b6b]`}></i>
                        </div>
                        <p className="text-[#1a2332] font-medium text-xs md:text-sm">{feature}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Benefits */}
                <div className="bg-white rounded-2xl p-5 md:p-8 shadow-sm">
                  <h2 className="text-xl md:text-2xl font-bold text-[#1a2332] mb-4 md:mb-6" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                    수강 혜택
                  </h2>
                  <div className="space-y-3 md:space-y-4">
                    {course.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-3 md:gap-4 p-3 md:p-4 bg-[#f7f7f5] rounded-xl">
                        <div className="w-7 h-7 md:w-8 md:h-8 bg-[#4ecdc4]/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-gift-line text-sm md:text-base text-[#4ecdc4]"></i>
                        </div>
                        <p className="text-gray-700 text-sm md:text-base">{benefit}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-4 md:space-y-6">
                {/* Instructor Card */}
                <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm">
                  <h3 className="text-base md:text-lg font-bold text-[#1a2332] mb-3 md:mb-4">담당 강사</h3>
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#4ecdc4]/30 to-[#ff6b6b]/30 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-xl md:text-2xl text-[#1a2332]"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-[#1a2332] text-sm md:text-base">{course.instructor}</p>
                      <p className="text-xs md:text-sm text-gray-500">후니국담 국어학원</p>
                    </div>
                  </div>
                </div>

                {/* Schedule Card */}
                <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm">
                  <h3 className="text-base md:text-lg font-bold text-[#1a2332] mb-3 md:mb-4">수업 정보</h3>
                  <div className="space-y-3 md:space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 md:w-10 md:h-10 bg-[#ff6b6b]/10 rounded-lg flex items-center justify-center">
                        <i className="ri-time-line text-sm md:text-base text-[#ff6b6b]"></i>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">수업 시간</p>
                        <p className="font-medium text-[#1a2332] text-sm md:text-base">{course.schedule}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 md:w-10 md:h-10 bg-[#4ecdc4]/10 rounded-lg flex items-center justify-center">
                        <i className="ri-group-line text-sm md:text-base text-[#4ecdc4]"></i>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">정원</p>
                        <p className="font-medium text-[#1a2332] text-sm md:text-base">{course.capacity}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 md:w-10 md:h-10 bg-[#ffd93d]/10 rounded-lg flex items-center justify-center">
                        <i className="ri-calendar-line text-sm md:text-base text-[#ffd93d]"></i>
                      </div>
                      <div>
                        <p className="text-xs md:text-sm text-gray-500">수업 기간</p>
                        <p className="font-medium text-[#1a2332] text-sm md:text-base">{course.duration}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Card */}
                <div className="bg-[#1a2332] rounded-2xl p-5 md:p-6 text-white">
                  <h3 className="text-base md:text-lg font-bold mb-2">수강 상담 받기</h3>
                  <p className="text-xs md:text-sm text-gray-300 mb-3 md:mb-4">
                    궁금한 점이 있으시면 언제든 문의해주세요
                  </p>
                  <button
                    onClick={() => navigate('/#contact')}
                    className="w-full py-2.5 md:py-3 bg-[#ff6b6b] text-white text-sm md:text-base rounded-lg font-semibold hover:bg-[#ff5252] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    상담 신청하기
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Curriculum Tab */
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-[#1a2332] mb-3 md:mb-4" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  단계별 학습 로드맵
                </h2>
                <p className="text-gray-600 text-sm md:text-base">체계적인 커리큘럼으로 목표 달성을 도와드립니다</p>
              </div>

              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                <div className="space-y-6 md:space-y-8">
                  {course.curriculum.map((item, index) => (
                    <div key={index} className="relative pl-12 md:pl-20">
                      {/* Timeline Node */}
                      <div className="absolute left-0 md:left-4 w-8 h-8 bg-white border-4 border-[#ff6b6b] rounded-full flex items-center justify-center z-10">
                        <span className="text-xs font-bold text-[#ff6b6b]">{index + 1}</span>
                      </div>

                      {/* Content Card */}
                      <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-3 md:mb-4">
                          <span className="px-2.5 md:px-3 py-1 bg-[#ff6b6b] text-white text-xs rounded-full font-medium">
                            {item.week}
                          </span>
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-[#1a2332] mb-3 md:mb-4">{item.title}</h3>
                        <ul className="space-y-2 md:space-y-3">
                          {item.contents.map((content, contentIndex) => (
                            <li key={contentIndex} className="flex items-start gap-2 md:gap-3">
                              <div className="w-1.5 h-1.5 bg-[#ff6b6b] rounded-full mt-1.5 md:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-600 text-sm md:text-base">{content}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom CTA */}
              <div className="mt-12 md:mt-16 bg-gradient-to-r from-[#1a2332] to-[#2a3342] rounded-2xl p-6 md:p-8 text-center">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  지금 바로 시작하세요
                </h3>
                <p className="text-gray-300 text-sm md:text-base mb-4 md:mb-6">
                  체계적인 커리큘럼으로 국어 실력을 한 단계 끌어올리세요
                </p>
                <button
                  onClick={() => navigate('/#contact')}
                  className="px-6 md:px-8 py-3 md:py-4 bg-[#ff6b6b] text-white text-sm md:text-base rounded-full font-semibold hover:bg-[#ff5252] transition-colors cursor-pointer whitespace-nowrap"
                >
                  수강 상담 신청하기
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2332] py-6 md:py-8">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <p className="text-gray-400 text-xs md:text-sm">
            © 2025 후니국담. All rights reserved. | 
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="ml-2 hover:text-white transition-colors cursor-pointer">
              Website Builder
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
